import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowOutward from '@mui/icons-material/ArrowOutward';
import Link from 'next/link';
import type {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import DisplayImage from '../display-image';
import {borderRadius, colors, fontSize, fontWeight, overlay, shadows, typography} from '@/styles/tokens';

interface FeatureCard {
    title: string;
    points: string[];
    /** Path to a line-icon SVG. Without one, the card shows its number instead. */
    icon?: string;
    /** Internal page the title links to. */
    href?: string;
}

interface IosFeatureCardsProps {
    heading: string;
    subheading: string;
    cards: FeatureCard[];
    /** With an image, the cards sit two-by-two beside it; without, they run in one row. */
    imageSrc?: string | StaticImageData;
    imageAlt?: string;
    ctaText?: string;
    onCtaClick?: () => void;
}

const gridItemSx = {display: 'flex'} as const;

const imageWrapSx = {
    position: 'relative',
    width: '100%',
    minHeight: {xs: 280, md: '100%'},
    aspectRatio: {xs: '1 / 1', md: 'auto'},
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
    boxShadow: shadows.card
} as const;

// The header block drops the standard 64rem cap and spans the full container, so long
// headings stay on one line on desktop. The default cap is responsive (`{xs, md}`), so the
// override must be too — a plain value would leave the `md` media-query rule in force.
const headerWrapSx = {maxWidth: {xs: 'none', md: 'none'}} as const;

const cardSx = {
    width: '100%',
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.xl,
    boxShadow: shadows.card,
    p: {xs: 2, md: 2.25},
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, background-color 0.25s ease',
    // On hover the card fills deep blue and its text, badge and icon flip to white.
    '&:hover': {transform: 'translateY(-6px)', boxShadow: shadows.lg, borderColor: colors.primary.deep, bgcolor: colors.primary.deep},
    '&:hover .feature-card-text': {color: colors.text.inverse},
    '&:hover .feature-card-badge': {bgcolor: overlay.whiteMid},
    '&:hover .feature-card-icon': {bgcolor: colors.text.inverse},
    '@media (prefers-reduced-motion: reduce)': {'&:hover': {transform: 'none'}}
} as const;

const badgeSx = {
    width: 48,
    height: 48,
    borderRadius: borderRadius.md,
    bgcolor: colors.background.panelBlue,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 1.5,
    transition: 'background-color 0.25s ease'
} as const;

// The supplied icons are black-stroked; a CSS mask recolours them to brand blue.
const iconSx = (src: string) =>
    ({
        width: 26,
        height: 26,
        bgcolor: colors.primary.main,
        maskImage: `url('${src}')`,
        WebkitMaskImage: `url('${src}')`,
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        transition: 'background-color 0.25s ease'
    } as const);

const numberSx = {color: colors.primary.main, fontSize: fontSize[16], fontWeight: fontWeight.bold, transition: 'color 0.25s ease'} as const;

const titleSx = {...typography.cardLabel, color: colors.text.primary, mb: 1.25, transition: 'color 0.25s ease'} as const;

const titleLinkSx = {color: 'inherit'} as const;

const pointListSx = {listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1} as const;

const pointSx = {
    ...typography.bodySm,
    color: colors.text.primary,
    position: 'relative',
    pl: 2,
    transition: 'color 0.25s ease',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '0.6rem',
        width: 6,
        height: 6,
        borderRadius: borderRadius.circle,
        bgcolor: colors.accent.orange
    }
} as const;

const ctaWrapSx = {textAlign: 'center', mt: {xs: 4, md: 5}} as const;

const renderCard = (card: FeatureCard, idx: number) => (
    <Box sx={cardSx}>
        <Box className="feature-card-badge" sx={badgeSx} aria-hidden="true">
            {card.icon ? (
                <Box className="feature-card-icon" sx={iconSx(card.icon)} />
            ) : (
                <Typography component="span" className="font-headers feature-card-text" sx={numberSx}>
                    {String(idx + 1).padStart(2, '0')}
                </Typography>
            )}
        </Box>
        <Typography component="h3" className="font-headers feature-card-text" sx={titleSx}>
            {card.href ? (
                <Box component={Link} href={card.href} sx={titleLinkSx}>
                    {card.title}
                </Box>
            ) : (
                card.title
            )}
        </Typography>
        <Box component="ul" sx={pointListSx}>
            {card.points.map((point) => (
                <Typography component="li" key={point} className="font-regular-text feature-card-text" sx={pointSx}>
                    {point}
                </Typography>
            ))}
        </Box>
    </Box>
);

const IosFeatureCards = ({heading, subheading, cards, imageSrc, imageAlt, ctaText, onCtaClick}: IosFeatureCardsProps) => (
    <Box>
        <MUIContainer>
            <SectionHeader heading={heading} subheading={subheading} wrapperSx={headerWrapSx} />

            {imageSrc ? (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={5} sx={gridItemSx}>
                        <Box sx={imageWrapSx}>
                            <DisplayImage
                                src={imageSrc}
                                alt={imageAlt ?? heading}
                                fill
                                sizes="(max-width: 900px) 100vw, 40vw"
                                style={{objectFit: 'cover'}}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Grid container spacing={3}>
                            {cards?.map((card, idx) => (
                                <Grid item xs={12} sm={6} key={card.title} sx={gridItemSx}>
                                    {renderCard(card, idx)}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            ) : (
                <Grid container spacing={3}>
                    {cards?.map((card, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={card.title} sx={gridItemSx}>
                            {renderCard(card, idx)}
                        </Grid>
                    ))}
                </Grid>
            )}

            {ctaText && onCtaClick && (
                <Box sx={ctaWrapSx}>
                    <Button
                        variant="contained"
                        disableRipple={true}
                        className="animated-button button-bg-primary"
                        onClick={onCtaClick}
                        endIcon={<ArrowOutward fontSize="small" />}
                    >
                        {ctaText}
                    </Button>
                </Box>
            )}
        </MUIContainer>
    </Box>
);

export default memo(IosFeatureCards);
